/**
 * Module 1.2 — the NET/VAT/GROSS engine (replit.md L11). ONE VAT truth for the
 * whole app, built on top of the `splitVat` primitive (module 1.1) — never
 * duplicating its cent-exact maths.
 *
 * What lives here:
 *   - the rate POLICY: a named, *configurable* set of whole-number rates
 *     (EE 2026: standard 24 / reduced 13 / reduced 9 / zero 0). The standard
 *     rate exists in exactly one place (`EE_VAT_POLICY_2026`); computation takes
 *     a policy, so the rate is a parameter, never hardcoded in arithmetic (L11).
 *   - `resolveVatRate(mode, …)` — maps a `VatMode` to its rate under a policy.
 *   - `applyVat` / `extractVat` / `computeVat` — policy-aware net↔gross, i.e. the
 *     `/1.24` extraction rule generalized to any rate, returning the canonical
 *     `Money`.
 *   - `summarizeKmd` — the KMD net primitive: output (sales) VAT minus input
 *     (purchase) VAT, with taxable bases grouped by rate (declaration boxes).
 *
 * Out of scope (deliberately): the full official KMD declaration form and the
 * KMD/TSD forecast are module 1.12, which CONSUMES this engine.
 */

import type { Money, VatMode } from './money';
import { isWholeCents, foreignCentsToEur } from './money';
import { splitVat, moneyFromNetGross, type VatBasis } from './vat';

/**
 * A configurable set of whole-number VAT rates (percent). Rates change (EE went
 * 20 → 22 → 24 in two years), so they are data, never literals in maths (L11).
 */
export interface VatPolicy {
  /** Standard domestic rate, e.g. 24. */
  readonly standard: number;
  /** Reduced rate for accommodation, e.g. 13. */
  readonly reducedAccommodation: number;
  /** Reduced rate for press/publications & listed goods, e.g. 9. */
  readonly reducedPress: number;
  /** The zero rate (foreign / exempt), normally 0. */
  readonly zero: number;
}

/** Estonia, 2026. The single source of the standard rate. */
export const EE_VAT_POLICY_2026: VatPolicy = {
  standard: 24,
  reducedAccommodation: 13,
  reducedPress: 9,
  zero: 0,
};

/** Which reduced rate applies (the `reduced` mode is not 1:1 with a rate). */
export type ReducedRate = 'accommodation' | 'press';

/** Optional context resolving a `VatMode` to a concrete rate. */
export interface VatRateContext {
  /** Rate set to resolve against; defaults to {@link EE_VAT_POLICY_2026}. */
  policy?: VatPolicy;
  /** Required when the mode is `reduced` — which reduced rate. */
  reduced?: ReducedRate;
}

/**
 * Resolve the whole-number VAT rate for a mode under a policy. `domestic` →
 * standard; `foreign`/`exempt` → zero; `reduced` → the chosen reduced rate
 * (throws if none is given, so an ambiguous reduced line surfaces loudly).
 */
export function resolveVatRate(mode: VatMode, context: VatRateContext = {}): number {
  const { policy = EE_VAT_POLICY_2026, reduced } = context;
  if (mode === 'domestic') {
    return policy.standard;
  }
  if (mode === 'foreign' || mode === 'exempt') {
    return policy.zero;
  }
  // mode === 'reduced'
  if (!reduced) {
    throw new RangeError('resolveVatRate: reduced mode requires a reduced-rate kind (accommodation | press)');
  }
  return reduced === 'accommodation' ? policy.reducedAccommodation : policy.reducedPress;
}

/** Inputs for the explicit, basis-driven entry point. */
export interface VatComputeInput {
  /** The entered amount in integer cents (net or gross per `basis`). */
  amountCents: number;
  /** Whether `amountCents` is the net or the gross amount. */
  basis: VatBasis;
  /** The VAT treatment; also resolves the rate and is carried onto the `Money`. */
  mode: VatMode;
  /** Rate set to resolve against; defaults to {@link EE_VAT_POLICY_2026}. */
  policy?: VatPolicy;
  /** Required when `mode` is `reduced`. */
  reduced?: ReducedRate;
}

/**
 * Build the canonical `Money` from a mode-driven rate — the app-facing entry
 * point. Components never pick a rate or do VAT maths; they call this.
 */
export function computeVat(input: VatComputeInput): Money {
  const { amountCents, basis, mode, policy, reduced } = input;
  const vatRate = resolveVatRate(mode, { policy, reduced });
  return splitVat({ amountCents, basis, vatRate, vatMode: mode });
}

/** Add VAT on top of a NET amount (integer cents) under the mode's rate. */
export function applyVat(netCents: number, mode: VatMode, context: VatRateContext = {}): Money {
  return computeVat({ amountCents: netCents, basis: 'net', mode, policy: context.policy, reduced: context.reduced });
}

/**
 * Extract the VAT already inside a GROSS amount (integer cents) — the `/1.24`
 * rule generalized to the mode's rate.
 */
export function extractVat(grossCents: number, mode: VatMode, context: VatRateContext = {}): Money {
  return computeVat({ amountCents: grossCents, basis: 'gross', mode, policy: context.policy, reduced: context.reduced });
}

/** Whether a VAT amount is collected on sales (`output`) or paid on purchases (`input`). */
export type VatDirection = 'output' | 'input';

/** A single VAT-bearing line fed into the KMD summary. */
export interface VatEntry {
  /** The line's canonical money (net/vat in integer cents). */
  money: Money;
  /** Output (sales) or input (purchases). */
  direction: VatDirection;
}

/** A taxable base + its VAT, both integer cents (a KMD declaration box). */
export interface KmdBox {
  /** Net taxable base in integer cents. */
  net: number;
  /** VAT in integer cents. */
  vat: number;
}

/** The KMD net position with bases grouped by rate. */
export interface KmdSummary {
  /** Output (sales) VAT collected, total integer cents. */
  outputVat: number;
  /** Input (purchase) VAT deductible, total integer cents. */
  inputVat: number;
  /** Net VAT: output − input. Positive = payable, negative = refundable. */
  netPayable: number;
  /** Output taxable base + VAT grouped by whole-number rate. */
  outputByRate: Record<number, KmdBox>;
  /** Input taxable base + VAT grouped by whole-number rate. */
  inputByRate: Record<number, KmdBox>;
}

/**
 * Aggregate a ledger of VAT lines into the KMD net (output − input), cent-exact.
 * Bases are grouped by rate so module 1.12 can fill the declaration boxes. All
 * arithmetic is integer-cent addition/subtraction — never a float, never `*rate`.
 */
export function summarizeKmd(entries: readonly VatEntry[]): KmdSummary {
  let outputVat = 0;
  let inputVat = 0;
  const outputByRate: Record<number, KmdBox> = {};
  const inputByRate: Record<number, KmdBox> = {};

  for (const { money, direction } of entries) {
    if (!isWholeCents(money.net) || !isWholeCents(money.vat)) {
      throw new RangeError('summarizeKmd: every Money must be whole cents');
    }
    if (!Number.isInteger(money.vatRate) || money.vatRate < 0) {
      throw new RangeError(`summarizeKmd: vatRate must be a whole-number percentage >= 0, received ${money.vatRate}`);
    }
    const buckets = direction === 'output' ? outputByRate : inputByRate;
    const box = buckets[money.vatRate] ?? { net: 0, vat: 0 };
    box.net += money.net;
    box.vat += money.vat;
    buckets[money.vatRate] = box;
    if (direction === 'output') {
      outputVat += money.vat;
    } else {
      inputVat += money.vat;
    }
  }

  return { outputVat, inputVat, netPayable: outputVat - inputVat, outputByRate, inputByRate };
}

/** Money assembled from an external SALES net/gross pair, plus its inferred VAT position. */
export interface SalesNetGross {
  /** The canonical Money (net/vat/gross, integer cents). */
  money: Money;
  /** The position inferred from the residual: `foreign` when VAT is zero, else `domestic`. */
  vatMode: 'domestic' | 'foreign';
}

/**
 * Assemble Money from an external SALES net+gross pair (integer cents) AND infer its
 * VAT position from the residual, under a policy: a zero VAT residual (gross === net)
 * is a foreign/exempt 0% sale, otherwise the standard domestic rate applies. This is
 * the ONE place that decision lives — a sync connector calls this instead of comparing
 * net to gross itself, so VAT classification stays in finance-core (replit.md L9/L11),
 * never in a route, a component, or a connector mapper. The domestic rate comes from
 * the policy (never a literal — L11), and VAT is the residual (`gross − net`) so the
 * gross = net + vat identity holds EXACTLY even when the source rounded its own VAT
 * line. Sign-safe (a credit note keeps its sign). Throws (via {@link moneyFromNetGross})
 * on non-integer cents so bad external data surfaces instead of silently drifting.
 */
export function moneyFromSalesNetGross(
  netCents: number,
  grossCents: number,
  policy: VatPolicy = EE_VAT_POLICY_2026,
): SalesNetGross {
  const foreign = grossCents - netCents === 0;
  const vatMode = foreign ? 'foreign' : 'domestic';
  const vatRate = foreign ? policy.zero : policy.standard;
  return { money: moneyFromNetGross({ netCents, grossCents, vatRate, vatMode }), vatMode };
}

/** An external PURCHASE invoice's net/vat/gross(+rounding) amounts, integer cents. */
export interface PurchaseNetVatGrossInput {
  /** Net (taxable base) in integer cents — e.g. a SmartAccounts `amount`. */
  netCents: number;
  /**
   * The source system's own VAT amount in integer cents (e.g. SA `vatAmount`).
   * `null` when the source omitted its VAT line — the only identity-consistent
   * reading is then the residual `gross − net − rounding`, derived HERE (the one
   * place monetary arithmetic lives), never by a caller.
   */
  vatCents: number | null;
  /** Gross (total incl VAT and rounding) in integer cents (e.g. SA `totalAmount`). */
  grossCents: number;
  /** Rounding cents the source added on top of net+vat (e.g. SA `roundAmount`); default 0. */
  roundCents?: number;
  /** Rate set to classify against; defaults to {@link EE_VAT_POLICY_2026}. */
  policy?: VatPolicy;
}

/** Money assembled from an external PURCHASE invoice, plus its inferred VAT position. */
export interface PurchaseNetVatGross {
  /** The canonical Money (net/vat/gross, integer cents; gross = net + vat EXACTLY). */
  money: Money;
  /** Position inferred from the amounts: zero VAT → `foreign`, else the closest policy rate. */
  vatMode: 'domestic' | 'foreign' | 'reduced';
}

/**
 * Assemble Money from an external PURCHASE invoice's net + VAT + gross amounts
 * (integer cents) AND infer its VAT position under a policy — the purchase-side
 * sibling of {@link moneyFromSalesNetGross}, and the ONE place that classification
 * lives (replit.md L9/L11): a sync connector calls this instead of comparing or
 * recomputing amounts itself.
 *
 * Unlike sales (net+gross only), purchases carry the source's own VAT line, which
 * is the deductible INPUT VAT — so `vat` is taken from the source verbatim, never
 * re-derived (reduced 13/9-rate and reverse-charge invoices would be corrupted by
 * a standard-rate recompute). The source identity `net + vat + rounding = gross`
 * is verified first, so inconsistent external data surfaces loudly; the canonical
 * Money then keeps `gross` (the actual cash out, rounding included) and the
 * source's `vat`, with `net = gross − vat` — i.e. the rounding cents live in net,
 * preserving the gross = net + vat identity EXACTLY (an at-least-once mirror must
 * not drift a cent).
 *
 * The rate label: zero VAT → `foreign` at the policy's zero rate (covers 0% and
 * reverse-charge invoices, whose VAT line is 0 — the mapper records the
 * reverse-charge kind separately). Otherwise the policy rate whose exact
 * whole-percent VAT on the source net is CLOSEST to the source VAT (BigInt metric,
 * no float): row-level rounding in the source can drift the invoice total a cent
 * or two off any exact rate, and the nearest-rate label stays correct while the
 * amounts themselves remain the source's truth. Ties resolve standard-first.
 * Sign-safe (a purchase credit note keeps its sign). Throws on non-integer cents.
 */
export function moneyFromPurchaseNetVatGross(input: PurchaseNetVatGrossInput): PurchaseNetVatGross {
  const { netCents, grossCents, roundCents = 0, policy = EE_VAT_POLICY_2026 } = input;
  // A missing source VAT line → the residual (the identity check below then holds
  // by construction, but a fractional/inconsistent net-gross pair still throws).
  const vatCents = input.vatCents ?? grossCents - netCents - roundCents;
  for (const [label, value] of [
    ['net', netCents],
    ['vat', vatCents],
    ['gross', grossCents],
    ['round', roundCents],
  ] as const) {
    if (!isWholeCents(value)) {
      throw new RangeError(`moneyFromPurchaseNetVatGross: expected whole ${label} cents, received ${value}`);
    }
  }
  if (netCents + vatCents + roundCents !== grossCents) {
    throw new RangeError(
      `moneyFromPurchaseNetVatGross: net ${netCents} + vat ${vatCents} + round ${roundCents} !== gross ${grossCents}`,
    );
  }

  let vatMode: 'domestic' | 'foreign' | 'reduced';
  let vatRate: number;
  if (vatCents === 0) {
    vatMode = 'foreign';
    vatRate = policy.zero;
  } else {
    // Candidates in tie-break order: standard first, then the reduced rates.
    const candidates: ReadonlyArray<readonly ['domestic' | 'reduced', number]> = [
      ['domestic', policy.standard],
      ['reduced', policy.reducedAccommodation],
      ['reduced', policy.reducedPress],
    ];
    const vatTimes100 = BigInt(vatCents) * 100n;
    const net = BigInt(netCents);
    let best = candidates[0];
    let bestError = -1n;
    for (const candidate of candidates) {
      const diff = vatTimes100 - net * BigInt(candidate[1]);
      const error = diff < 0n ? -diff : diff;
      if (bestError < 0n || error < bestError) {
        best = candidate;
        bestError = error;
      }
    }
    vatMode = best[0];
    vatRate = best[1];
  }

  return {
    money: { net: grossCents - vatCents, vat: vatCents, gross: grossCents, vatRate, vatMode },
    vatMode,
  };
}

/** A purchase invoice's net/vat/round/gross amounts as integer cents in ONE currency. */
export interface PurchaseCentsSet {
  /** Net (taxable base) in integer cents. */
  netCents: number;
  /** The source's own VAT line in integer cents, or `null` when it omitted one. */
  vatCents: number | null;
  /** Rounding cents on top of net+vat. */
  roundCents: number;
  /** Gross (total incl VAT and rounding) in integer cents. */
  grossCents: number;
}

/**
 * Convert a FOREIGN-currency purchase invoice's amount set to EUR integer cents at
 * the document's own exchange rate (SmartAccounts prints it: FOREIGN units per 1
 * EUR), preserving the `net + vat + round === gross` identity EXACTLY after
 * rounding — the ONE place foreign purchase money is converted (L9), so the mapper
 * does no currency arithmetic and {@link moneyFromPurchaseNetVatGross} then holds by
 * construction. Fully deterministic (the rate rides on the invoice — no external
 * rate source, L6).
 *
 * The source identity is validated FIRST, in the invoice's own currency (the same
 * invariant the purchase builder enforces, applied pre-conversion — the builder can
 * only see the post-conversion EUR amounts, where the identity is guaranteed): an
 * inconsistent foreign invoice fails loudly instead of being silently reconciled.
 * Then gross, vat and rounding are each converted and NET is DERIVED
 * (`gross − vat − round`) so per-line rounding cents land in net and the identity is
 * exact — anchoring on gross (the actual EUR cash out) is what must be faithful.
 * When the source omitted its VAT line (`vatCents === null`) net is converted
 * directly and the builder derives the residual VAT. Sign-safe (credit notes keep
 * their sign). Throws (via {@link foreignCentsToEur}) on a bad rate or amount.
 */
export function convertForeignPurchaseCentsToEur(
  amounts: PurchaseCentsSet,
  exchangeRate: number,
): PurchaseCentsSet {
  const { netCents, vatCents, roundCents, grossCents } = amounts;
  if (vatCents !== null && netCents + vatCents + roundCents !== grossCents) {
    throw new RangeError(
      `convertForeignPurchaseCentsToEur: source net ${netCents} + vat ${vatCents} + round ${roundCents} !== gross ${grossCents}`,
    );
  }
  const eurGross = foreignCentsToEur(grossCents, exchangeRate);
  const eurRound = foreignCentsToEur(roundCents, exchangeRate);
  if (vatCents === null) {
    return {
      netCents: foreignCentsToEur(netCents, exchangeRate),
      vatCents: null,
      roundCents: eurRound,
      grossCents: eurGross,
    };
  }
  const eurVat = foreignCentsToEur(vatCents, exchangeRate);
  return {
    netCents: eurGross - eurVat - eurRound,
    vatCents: eurVat,
    roundCents: eurRound,
    grossCents: eurGross,
  };
}
